/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { API_URL } from '../../../modules/auth/core/_requests'
import { useAuth } from '../../../modules/auth'
import AIOptions, { AIOptionValues, defaultAIOptionValues } from './components/AIOptions'

interface ProductData {
  id: number
  title: string
  description: string
  variants: any[]
  images: string[]
}

interface BlogStatsData {
  storeInfo: {
    name: string
    shopUrl: string
    hasBlogConfigured: boolean
    blogReviewsId?: string
  }
  blogStats: {
    totalArticles: number
    lastChecked?: string
    error?: string
    details?: any
  }
}

export const ReviewWriterPage: FC = () => {
  const { currentUser } = useAuth()
  const currentStoreName = currentUser?.decoded?.current_shopify_store || ''

  // Form fields
  const [productUrl, setProductUrl] = useState('')
  const [aiOptions, setAiOptions] = useState<AIOptionValues>(defaultAIOptionValues)

  // Product data
  const [productData, setProductData] = useState<ProductData | null>(null)
  
  // Generated content
  const [extractedFeatures, setExtractedFeatures] = useState('')
  const [generatedReview, setGeneratedReview] = useState('')
  const [editedReview, setEditedReview] = useState('')

  // Loading states
  const [loading, setLoading] = useState(false)
  const [fetchingProduct, setFetchingProduct] = useState(false)
  const [extractingFeatures, setExtractingFeatures] = useState(false)
  const [generatingReview, setGeneratingReview] = useState(false)

  // Blog stats
  const [blogStats, setBlogStats] = useState<BlogStatsData | null>(null)
  const [loadingStats, setLoadingStats] = useState(false)

  // Alerts and notifications
  const [hasExistingReview, setHasExistingReview] = useState(false)
  const [existingReviewUrl, setExistingReviewUrl] = useState('')

  // Fetch blog stats function
  const fetchBlogStats = async () => {
    if (!currentStoreName) return
    
    setLoadingStats(true)
    try {
      const response = await axios.get<BlogStatsData>(`${API_URL}/api/shopify_review_writer/blog-stats/${currentStoreName}`)
      setBlogStats(response.data)
    } catch (error) {
      console.error('Failed to fetch blog stats:', error)
    } finally {
      setLoadingStats(false)
    }
  }

  // Fetch blog stats on component mount
  useEffect(() => {
    fetchBlogStats()
  }, [currentStoreName])

  // Fetch product data from URL
  const handleFetchProduct = async () => {
    if (!productUrl.trim()) {
      alert('Please enter a valid product URL.')
      return
    }

    setFetchingProduct(true)
    try {
      const response = await axios.post(`${API_URL}/api/shopify_review_writer/fetch-product`, {
        productUrl: productUrl.trim(),
      })
      
      const product = response.data.product
      setProductData(product)

      // Check if product already has a review
      if (currentStoreName && product.id) {
        try {
          const reviewCheckResponse = await axios.post(`${API_URL}/api/shopify_review_writer/check-existing-review`, {
            storeName: currentStoreName,
            productId: product.id,
          })
          
          setHasExistingReview(reviewCheckResponse.data.hasExistingReview)
          setExistingReviewUrl(reviewCheckResponse.data.reviewUrl || '')
          
          if (reviewCheckResponse.data.hasExistingReview) {
            alert('This product already has a review.')
          }
        } catch (error) {
          console.error('Failed to check existing review:', error)
        }
      }

      // Extract features from product description
      if (product.description) {
        await handleExtractFeatures(product.description)
      }

      // Auto-generate review
      await handleGenerateReview(product)

    } catch (error: any) {
      console.error('Failed to fetch product:', error)
      alert('Failed to fetch product data.')
    } finally {
      setFetchingProduct(false)
    }
  }

  // Extract features from description
  const handleExtractFeatures = async (description?: string) => {
    const desc = description || productData?.description || ''
    if (!desc.trim()) return

    setExtractingFeatures(true)
    try {
      const response = await axios.post(`${API_URL}/api/shopify_review_writer/extract-features`, {
        description: desc,
        ...aiOptions,
      })
      setExtractedFeatures(response.data.features)
    } catch (error: any) {
      console.error('Failed to extract features:', error)
      alert('Failed to extract features.')
    } finally {
      setExtractingFeatures(false)
    }
  }

  // Generate review content
  const handleGenerateReview = async (product?: ProductData) => {
    const prod = product || productData
    if (!prod || !currentStoreName) {
      alert('Please fetch product data first.')
      return
    }

    setGeneratingReview(true)
    try {
      const response = await axios.post(`${API_URL}/api/shopify_review_writer/generate-review`, {
        storeName: currentStoreName,
        title: prod.title,
        description: prod.description,
        imageUrl: prod.images.length > 0 ? prod.images[0] : '',
        ...aiOptions,
      })
      
      setGeneratedReview(response.data.reviewContent)
      setEditedReview(response.data.reviewContent)
    } catch (error: any) {
      console.error('Failed to generate review:', error)
      alert('Failed to generate review.')
    } finally {
      setGeneratingReview(false)
    }
  }

  // Push review to Shopify
  const handlePushReview = async () => {
    if (!productData || !editedReview.trim() || !currentStoreName) {
      alert('Please ensure you have product data and generated review.')
      return
    }

    setLoading(true)
    try {
      const reviewTitle = `${productData.title} Review`
      
      await axios.post(`${API_URL}/api/shopify_review_writer/push-review`, {
        storeName: currentStoreName,
        title: reviewTitle,
        reviewContent: editedReview.trim(),
        productId: productData.id,
        featuredImageUrl: productData.images.length > 0 ? productData.images[0] : '',
      })

      alert('Review successfully pushed to Shopify as draft!')
      
      // Refresh blog stats to show updated count
      fetchBlogStats()
      
      // Reset form
      setProductUrl('')
      setProductData(null)
      setExtractedFeatures('')
      setGeneratedReview('')
      setEditedReview('')
      setHasExistingReview(false)
      setExistingReviewUrl('')
      
    } catch (error: any) {
      console.error('Failed to push review:', error)
      const errorMessage = error.response?.data?.details || error.response?.data?.error || 'Failed to push review to Shopify.'
      alert(`Error: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }

  // Reset all form data
  const handleReset = () => {
    setProductUrl('')
    setProductData(null)
    setExtractedFeatures('')
    setGeneratedReview('')
    setEditedReview('')
    setHasExistingReview(false)
    setExistingReviewUrl('')
  }

  // Calculate review statistics
  const stats = useMemo(() => {
    const strippedText = editedReview.replace(/<[^>]+>/g, ' ').trim()
    const characters = strippedText.length
    const letters = (strippedText.match(/[a-zA-Z]/g) || []).length
    const wordsArray = strippedText.split(/\s+/).filter(Boolean)
    const words = wordsArray.length
    const sentences = strippedText.split(/[.!?]+/).filter((s) => s.trim().length > 0).length
    const paragraphs = strippedText.split(/\n+/).filter((s) => s.trim().length > 0).length
    
    const totalReadingSeconds = (words / 200) * 60
    const minutes = Math.floor(totalReadingSeconds / 60)
    const seconds = Math.round(totalReadingSeconds % 60)
    
    return {
      letters,
      characters,
      words,
      sentences,
      paragraphs,
      readingTime: `${minutes}m ${seconds}s`,
    }
  }, [editedReview])

  return (
    <>
      <h1 className='mt-3 ms-3'>Shopify Review Writer</h1>
      
      {/* Main Form Card */}
      <div className='card mx-3 mt-4'>
        <div className='card-body'>
          {/* Current Store Info */}
          <div className='mb-3'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <label className='form-label mb-0'>Current Store</label>
              {currentStoreName && (
                <button
                  className='btn btn-sm btn-outline-secondary'
                  onClick={fetchBlogStats}
                  disabled={loadingStats}
                  title='Refresh blog stats'
                >
                  <i className={`fa-solid fa-refresh ${loadingStats ? 'fa-spin' : ''}`}></i>
                </button>
              )}
            </div>
            <div className='d-flex align-items-center gap-3'>
              <div className='badge bg-primary fs-6 px-3 py-2'>
                {currentStoreName || 'No store selected'}
              </div>
              {loadingStats ? (
                <span className='text-muted'>Loading stats...</span>
              ) : blogStats ? (
                <div className='d-flex align-items-center gap-3'>
                  <span className={`text-${blogStats.storeInfo.hasBlogConfigured ? 'success' : 'warning'}`}>
                    <i className={`fa-solid fa-${blogStats.storeInfo.hasBlogConfigured ? 'check-circle' : 'exclamation-triangle'}`}></i>
                    {blogStats.storeInfo.hasBlogConfigured ? 'Review Blog Configured' : 'Review Blog Not Configured'}
                  </span>
                  {blogStats.storeInfo.hasBlogConfigured && (
                    <span className='text-info'>
                      <i className='fa-solid fa-star'></i>
                      {blogStats.blogStats.totalArticles} Review{blogStats.blogStats.totalArticles !== 1 ? 's' : ''}
                    </span>
                  )}
                  {blogStats.blogStats.error && (
                    <span className='text-danger' title={blogStats.blogStats.error}>
                      <i className='fa-solid fa-exclamation-circle'></i>
                      Error fetching stats
                    </span>
                  )}
                </div>
              ) : currentStoreName ? (
                <span className='text-muted'>Failed to load store info</span>
              ) : null}
            </div>
          </div>

          {/* Product URL Input */}
          <div className='mb-3'>
            <label className='form-label'>
              Shopify Product URL <span className='text-danger'>*</span>
            </label>
            <input
              type='text'
              className='form-control'
              value={productUrl}
              onChange={(e) => setProductUrl(e.target.value)}
              placeholder='Enter Shopify product URL...'
            />
          </div>

          {/* AI Options */}
          <AIOptions value={aiOptions} onChange={setAiOptions} />

          {/* Action Buttons */}
          <div className='mb-3 d-flex flex-wrap gap-2'>
            <button
              className='btn btn-primary'
              onClick={handleFetchProduct}
              disabled={fetchingProduct || !productUrl.trim() || !currentStoreName}
            >
              {fetchingProduct ? 'Fetching...' : 'Fetch Product Data'}
            </button>
            
            <button
              className='btn btn-success'
              onClick={() => handleGenerateReview()}
              disabled={generatingReview || !productData}
            >
              {generatingReview ? 'Generating...' : 'Generate Review'}
            </button>
            
            <button
              className='btn btn-warning'
              onClick={handlePushReview}
              disabled={loading || !editedReview.trim() || !productData}
            >
              {loading ? 'Pushing...' : 'Push Review to Shopify'}
            </button>
            
            <button
              className='btn btn-outline-secondary'
              onClick={handleReset}
              disabled={loading || fetchingProduct || generatingReview}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Product Information Card */}
      {productData && (
        <div className='card mx-3 mt-2'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <span>Product Information</span>
            {hasExistingReview && (
              <span className='badge bg-warning'>
                Review Already Exists
                {existingReviewUrl && (
                  <a 
                    href={existingReviewUrl} 
                    target='_blank' 
                    rel='noopener noreferrer'
                    className='text-white ms-2'
                  >
                    View
                  </a>
                )}
              </span>
            )}
          </div>
          <div className='card-body'>
            <h5>{productData.title}</h5>
            <p><strong>Product ID:</strong> {productData.id}</p>
            <p><strong>Variants:</strong> {productData.variants.length}</p>
            <p><strong>Images:</strong> {productData.images.length}</p>
            {productData.images.length > 0 && (
              <div className='mt-2'>
                <strong>Featured Image:</strong>
                <br />
                <img 
                  src={productData.images[0]} 
                  alt='Product' 
                  style={{ maxWidth: '200px', maxHeight: '200px' }}
                  className='mt-2'
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content and Preview */}
      <div className='row mx-3 mt-3'>
        {/* Extracted Features */}
        <div className='col-md-4 mb-3'>
          <div className='card'>
            <div className='card-header'>Extracted Key Features</div>
            <div className='card-body' style={{ minHeight: '300px' }}>
              {extractingFeatures ? (
                <div className='text-muted'>Extracting features...</div>
              ) : extractedFeatures ? (
                <div style={{ whiteSpace: 'pre-wrap' }}>{extractedFeatures}</div>
              ) : (
                <p className='text-muted'>No features extracted yet.</p>
              )}
            </div>
          </div>
          
          {/* Review Stats */}
          {editedReview && (
            <div className='card mt-3'>
              <div className='card-header'>Review Stats</div>
              <div className='card-body'>
                <p><strong>Letters:</strong> {stats.letters}</p>
                <p><strong>Characters:</strong> {stats.characters}</p>
                <p><strong>Words:</strong> {stats.words}</p>
                <p><strong>Sentences:</strong> {stats.sentences}</p>
                <p><strong>Paragraphs:</strong> {stats.paragraphs}</p>
                <p><strong>Reading Time:</strong> {stats.readingTime}</p>
              </div>
            </div>
          )}
        </div>

        {/* Editable Review HTML */}
        <div className='col-md-4 mb-3'>
          <div className='card'>
            <div className='card-header'>Editable Review HTML</div>
            <div className='card-body' style={{ minHeight: '300px' }}>
              {generatingReview ? (
                <div className='text-muted'>Generating review...</div>
              ) : (
                <textarea
                  className='form-control'
                  style={{ height: '300px', fontFamily: 'monospace', fontSize: '12px' }}
                  value={editedReview}
                  onChange={(e) => setEditedReview(e.target.value)}
                  placeholder='Generated review HTML will appear here...'
                />
              )}
            </div>
          </div>
        </div>

        {/* Rendered Preview */}
        <div className='col-md-4 mb-3'>
          <div className='card'>
            <div className='card-header'>Review Preview</div>
            <div className='card-body' style={{ minHeight: '300px', overflowY: 'auto' }}>
              {generatingReview ? (
                <div className='text-muted'>Generating review...</div>
              ) : editedReview ? (
                <div dangerouslySetInnerHTML={{ __html: editedReview }} />
              ) : (
                <p className='text-muted'>No review content generated yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export const ShopifyReviewWriterWrapper: FC = () => {
  return <ReviewWriterPage />
}
